// Session gate — the single source of truth for "do we have a usable session?"
//
// This module deliberately has NO imports. It sits below both the API layer and the
// stores so either can depend on it without a cycle: `api/client.ts` and `api/watch.ts`
// read it, `api/session.ts` drives it.
//
// ============================================================================
// Why a state machine and not "is an auth request in flight?"
// ============================================================================
// The previous implementation closed a promise gate whenever an auth request started and
// resolved it in that request's `finally`. That tracks a TRANSPORT fact — "an auth call is
// happening" — when the thing every other request actually needs is a STATE fact — "a
// session exists".
//
// The two differ whenever establishing a session takes more than one call. A stale stored
// token means `token_login` fails and `create_account` follows; the old gate opened after
// the first one, and every queued request flooded out into the gap before the second
// re-closed it. In production that was 130 events across 27 users in six hours, 65% of all
// errors, in two flavours: `401 session_id_missing` for requests that left before the
// session existed, and `403` for the ones that left in the microsecond after the cookie
// landed but with headers built before it.
//
// So: the gate closes because there is no usable session, never merely because an auth
// request is in flight. A background token renewal runs with the gate wide open, because
// the session it is renewing already works.

/**
 * `unavailable` is a settled state, not a failure state — the gate OPENS on it.
 *
 * Gating on success rather than settlement is the trap: it turns any auth failure into a
 * permanent hang. Worse, requests wait inside header construction, before any
 * `AbortSignal` exists, so such a hang never even times out — no request, no error, no
 * Sentry event, just a page that never finishes.
 */
export type SessionState = "idle" | "establishing" | "ready" | "unavailable";

type Waiter = () => void;

let state: SessionState = "idle";
let waiters: Waiter[] = [];
/**
 * Bumped every time a session is established. Lets a request tell "the session I used is
 * broken" apart from "the session I used has already been replaced while I was in flight".
 */
let generation = 0;

/**
 * Set once by `api/session.ts`, so the gate can start a flow without importing it.
 *
 * `authoritative` distinguishes the two reasons to run it. A first start may take the
 * optimistic route (a returning visitor's cookie is probably still good, so open the gate
 * with no network call at all), but a recovery may not: it runs precisely because a 401
 * proved the session is gone, so assuming one exists would make recovery a no-op.
 */
let starter: ((options: { authoritative: boolean }) => Promise<void>) | null = null;
/** The single in-flight flow. Concurrent callers join it rather than starting another. */
let inflight: Promise<void> | null = null;
/** Whether the in-flight run may skip the network (see `starter`). */
let inflightAuthoritative = false;

export function getSessionState(): SessionState {
  return state;
}

/** True once the session has settled — succeeded OR definitively failed. */
function isSettled(): boolean {
  return state === "ready" || state === "unavailable";
}

function release(): void {
  const pending = waiters;
  waiters = [];
  pending.forEach(resolve => resolve());
}

// ============================================================================
// Transitions — called only by the flow in api/session.ts
// ============================================================================

/**
 * Enter `establishing`.
 *
 * Only ever reached with no usable session: from `whenSessionSettled` while `idle`, or from
 * `reestablishSession`, which forces `idle` first because a 401 proved the session is gone.
 * A user-initiated login does NOT come through here — the store calls `requireLoginSession` once
 * a token comes back, which may only open the gate and never close it — so a login cannot take
 * the gate away from a page that was working, and there is no previous-good-session case to
 * restore.
 */
export function beginEstablishing(): void {
  state = "establishing";
}

export function markReady(): void {
  state = "ready";
  generation += 1;
  release();
}

/**
 * Which session a request is running against. Capture it before sending; if it has moved on
 * by the time a 401 comes back, somebody else already re-authenticated and this request
 * only needs replaying, not another round trip.
 *
 * Without this, "exactly one re-auth per outage" would hold only because concurrent 401s
 * arrive inside one round trip — true in practice, but timing rather than structure, and a
 * straggler landing after recovery would start a redundant flow.
 */
export function getSessionGeneration(): number {
  return generation;
}

/**
 * The flow finished without a session. The gate still OPENS — see `SessionState`.
 *
 * Takes no reason: whatever went wrong has already been reported to Sentry by the flow,
 * and duplicating it here would be a second copy of the same fact that nothing reads.
 */
export function markUnavailable(): void {
  state = "unavailable";
  release();
}

// ============================================================================
// Single flight
// ============================================================================

/** Registered once at app init so this module never has to import the flow. */
export function setSessionStarter(fn: (options: { authoritative: boolean }) => Promise<void>): void {
  starter = fn;
}

/**
 * Run the session flow, joining an existing run instead of starting a second one.
 *
 * Deduplication is essential rather than an optimisation: a page whose category strip
 * fans out twenty concurrent requests will see all twenty fail with 401 at once, and every
 * one of them asks for re-authentication. Exactly one `token_login` must go out.
 *
 * Note this does NOT touch the gate. Whether to close it is the caller's decision —
 * a background renewal joins the same flow with the gate left open, while a 401 recovery
 * closes it first. Keeping the two separate is what allows both to share one flight.
 */
function runSessionFlow(authoritative: boolean): Promise<void> {
  // Joining is only correct when the run already under way is at least as strong as what
  // this caller needs. An authoritative caller (a 401 recovery) that joined an optimistic
  // run would be "recovered" by a flow that never touched the network — so it waits for
  // that run and then does its own.
  if (inflight && (!authoritative || inflightAuthoritative)) {
    return inflight;
  }

  const previous = inflight ?? Promise.resolve();
  const start = starter;
  inflightAuthoritative = authoritative;

  inflight = previous
    .then(() => {
      if (!start) {
        // Registration happens at module init; reaching here means a request beat it.
        throw new Error("session flow used before setSessionStarter()");
      }
      return start({ authoritative });
    })
    // The flow reports its own failures. Swallowing here keeps every caller — including
    // `request()`, which awaits this before deciding whether to replay — from having to
    // guard against a rejection that carries no information it can act on.
    .catch(() => {})
    .finally(() => {
      inflight = null;
      // INVARIANT: a run that happened leaves the gate settled. Without this, a flow that
      // returns without calling markReady/markUnavailable leaves every waiter stranded —
      // and they wait inside header construction, before any AbortSignal exists, so the
      // page hangs with no request, no error and no Sentry event. A background renewal
      // whose catch deliberately does nothing is exactly such a flow.
      if (state === "establishing") {
        markUnavailable();
      }
    });

  return inflight;
}

/**
 * Re-establish a session that has stopped working (a request came back 401).
 *
 * Closes the gate first — unlike a renewal, here we know there is no usable session, so
 * anything issued now would fail too.
 */
export function reestablishSession(): Promise<void> {
  // Leave `ready` so concurrent callers queue behind the recovery instead of sailing
  // through on a session we now know is broken.
  beginEstablishing();
  return runSessionFlow(true);
}

// ============================================================================
// Waiting
// ============================================================================

/**
 * Resolve once the session has settled, starting the flow if nothing has yet.
 *
 * Self-starting is deliberate: it means correctness does not depend on some component
 * remembering to kick off the bootstrap. `useInitializeUser` becomes a warm-up that gets
 * the work going earlier, not a prerequisite, and skipping it (the coming-soon path)
 * cannot wedge anything.
 */
export function whenSessionSettled(): Promise<void> {
  if (isSettled()) {
    return Promise.resolve();
  }

  const waiting = new Promise<void>(resolve => {
    waiters.push(resolve);
  });

  if (state === "idle") {
    beginEstablishing();
    // Not authoritative: this is the first start, so the optimistic route is allowed.
    // It matters that this path can be optimistic at all — React flushes child effects
    // before parent ones, so a component's data request reliably reaches here BEFORE
    // `useInitializeUser` in the root layout gets to warm the session up. If only the
    // warm-up could be optimistic, the optimistic path would never actually run.
    // `runSessionFlow` never rejects and always settles the gate, so there is nothing to
    // catch here and no way for this to strand the waiter created above.
    void runSessionFlow(false);
  }

  return waiting;
}

/** Test/debug helper. Not used by application code. */
export function resetSessionGateForTest(): void {
  state = "idle";
  waiters = [];
  inflight = null;
  inflightAuthoritative = false;
  generation = 0;
}
